import {
  APP_INITIALIZER,
  ApplicationConfig,
  provideZoneChangeDetection,
  ErrorHandler,
  LOCALE_ID,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
} from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';

// Routes
import { appRoutes } from './app.routes';

// PrimeNg
import Aura from '@primeng/themes/aura';
import { providePrimeNG } from 'primeng/config';
import { ConfirmationService, MessageService } from 'primeng/api';

// Interceptor
import { loadingInterceptor } from './shared/components/general/loading/interceptor/loading.interceptor';
import { tokenInterceptor } from './interceptors/token.interceptor';
import { errorInterceptor } from './interceptors/error.interceptor';
import { authInterceptor } from './interceptors/auth.interceptor';
// Services
import { InactivityManagerService } from './Services/monitoryInactivity/inactivity-manager.service';
import { AuthService } from './Services/auth.service';
import { DialogService } from 'primeng/dynamicdialog';

// Handlers
import { GlobalErrorHandler } from './handlers/global-error-handler';
import { LocalDateTimeInterceptor } from './interceptors/local-datetime.interceptor';
import { definePreset } from '@primeng/themes';

const Inviare = definePreset(Aura, {
  semantic: {
    colorScheme: {
      light: {
        surface: {
          0: '{gray.50}',
          50: '{gray.50}',
          100: '{gray.100}',
          200: '{gray.200}',
          300: '{gray.300}',
          400: '{gray.400}',
          500: '{gray.500}',
          600: '{gray.600}',
          700: '{gray.700}',
          800: '{gray.800}',
          900: '{gray.900}',
          950: '{gray.950}'
        },
        content: {
          background: '{surface.100}',
          focusBackground: '{surface.100}',
          color: '{gray.950}',
          focusColor: '{gray.950}'
        }
      },

      dark: {
        surface: {
          0: '{gray.950}',
          50: '{gray.900}',
          100: '{gray.800}',
          200: '{gray.700}',
          300: '{gray.600}',
          400: '{gray.500}',
          500: '{gray.400}',
          600: '{gray.300}',
          700: '{gray.200}',
          800: '{gray.100}',
          900: '{gray.50}',
          950: '{gray.50}'
        },
        content: {
          background: '{surface.100}',
          focusBackground: '{surface.100}',
          color: '{gray.50}',
          focusColor: '{gray.50}'
        }
      }
    }
  }
});

// Factory para inicializar el servicio de inactividad
// Esta función se ejecutará una sola vez al inicio de la aplicación
function initializeInactivityMonitoring(
  inactivityManager: InactivityManagerService,
  authService: AuthService
) {
  return () => {
    try {
      // Verificar si el usuario está autenticado antes de iniciar el monitoreo
      const token = authService.isAuthenticated;
      if (token) {
        inactivityManager.startMonitoring();
      } 
    } catch (error) {}
    return Promise.resolve();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'es-CO' },
    // Registrar el manejador de errores global
    { provide: ErrorHandler, useClass: GlobalErrorHandler },

    // Inicializar el servicio de inactividad
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
      withEnabledBlockingInitialNavigation()
    ),
    provideAnimationsAsync(),
    provideHttpClient(
      withInterceptors([
        loadingInterceptor,
        LocalDateTimeInterceptor,
        tokenInterceptor,
        errorInterceptor,
        authInterceptor
      ])
    ),
    MessageService,
    ConfirmationService,
    DialogService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeInactivityMonitoring,
      deps: [InactivityManagerService, AuthService],
      multi: true,
    },
   providePrimeNG({
      theme: {
        preset: Inviare,
        options: {
          darkModeSelector: '.dark'
        }
      }
    })
  ],
};
